package one.healthium.uscreen.ui.blocks

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import one.healthium.ui.compose.components.AppIcons
import one.healthium.uscreen.ui.util.clickableHapticOnPress
import one.healthium.ui.compose.theme.AppTheme
import one.healthium.uscreen.domain.models.ChoiceAnimation
import one.healthium.uscreen.domain.models.ChoiceListBlock
import one.healthium.uscreen.domain.models.UScreenChoiceOption
import one.healthium.uscreen.ui.UScreenAnswer
import one.healthium.uscreen.ui.animation.POST_TRANSITION_DELAY
import one.healthium.uscreen.ui.animation.staggerSlideIn
import one.healthium.uscreen.ui.components.pressScale
import one.healthium.uscreen.ui.util.uscreenPadding

@Composable
fun UScreenChoiceListBlock(
    modifier: Modifier = Modifier,
    block: ChoiceListBlock,
    answers: Map<String, UScreenAnswer>,
    isMale: Boolean?,
    onChoiceSelected: (key: String, optionKey: String, single: Boolean) -> Unit,
    onAutoAdvance: () -> Unit,
) {
    val selectedKeys = (answers[block.key] as? UScreenAnswer.ChoiceAnswer)?.selectedKeys.orEmpty()
    val scope = rememberCoroutineScope()

    Column(
        modifier = modifier
            .fillMaxWidth()
            .uscreenPadding(block.padding),
        verticalArrangement = Arrangement.spacedBy(12.dp),
    ) {
        block.options.forEachIndexed { index, option ->
            val isSelected = option.key in selectedKeys
            val itemModifier = if (block.animation == ChoiceAnimation.Stagger) {
                Modifier.staggerSlideIn(index, initialDelay = POST_TRANSITION_DELAY)
            } else {
                Modifier
            }
            UScreenChoiceItem(
                option = option,
                isMale = isMale,
                isSelected = isSelected,
                onClick = {
                    onChoiceSelected(block.key, option.key, block.single)
                    if (block.single) {
                        scope.launch {
                            delay(150)
                            onAutoAdvance()
                        }
                    }
                },
                modifier = itemModifier,
            )
        }
    }
}

@Composable
fun UScreenChoiceItem(
    option: UScreenChoiceOption,
    isMale: Boolean?,
    isSelected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val borderColor = if (isSelected) AppTheme.uscreenStyle.choiceBorderSelected else AppTheme.uscreenStyle.choiceBorder
    val borderWidth = if (isSelected) 2.dp else 1.dp

    Surface(
        modifier = modifier
            .fillMaxWidth()
            .height(74.dp)
            .pressScale()
            .testTag("uscreen_choice_item"),
        shape = RoundedCornerShape(16.dp),
        color = AppTheme.uscreenStyle.choiceBackground,
        border = BorderStroke(borderWidth, borderColor),
    ) {
        Row(
            modifier = Modifier
                .clickableHapticOnPress(onClick = onClick)
                .padding(horizontal = 16.dp),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            val emoji = option.emoji?.resolve(isMale)
            if (emoji != null) {
                Text(
                    text = emoji,
                    style = AppTheme.textStyles.subTitle,
                )
                Spacer(Modifier.width(12.dp))
            }

            Text(
                text = option.text.resolve(isMale),
                style = AppTheme.textStyles.body1Medium,
                modifier = Modifier.weight(1f),
            )

            Spacer(Modifier.width(12.dp))
            ChoiceIndicator(isSelected = isSelected)
        }
    }
}

@Composable
private fun ChoiceIndicator(isSelected: Boolean) {
    if (isSelected) {
        AppIcons.icCheckmark(AppTheme.colors.primary)
    } else {
        Surface(
            modifier = Modifier.size(24.dp),
            shape = RoundedCornerShape(12.dp),
            color = Color.Transparent,
            border = BorderStroke(1.5.dp, AppTheme.uscreenStyle.choiceIndicatorBorder),
        ) {}
    }
}
